import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import { INTEGRATION_OAUTH_DONE } from './ThirdPartyOAuthCallback';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 300000;

interface ConnectToProviderProps {
  provider: string;
  onSuccess?: () => void;
  children?: (props: { onClick: () => void; isConnecting: boolean }) => React.ReactNode;
}

const ConnectToProvider: React.FC<ConnectToProviderProps> = ({
  provider,
  onSuccess,
  children,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const { fetchConnections } = useStoreThirdPartyIntegration();
  const setNotification = useNotificationStore(state => state.setNotification);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popupRef = useRef<Window | null>(null);

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const loginErrorHandler = (err: unknown) => {
    setNotification({
      type: NotificationType.Error,
      message: getAPIErrorMessage(err),
    });
  };

  const connectionHandler = useCallback(async () => {
    clearPolling();

    try {
      const response = await ThirdPartyIntegrationService.getInstallUrl(provider);
      const url = response?.data?.installUrl ?? (response as any)?.installUrl;
      if (!url) {
        setNotification({
          type: NotificationType.Error,
          message: `Could not load ${provider} connection URL.`,
        });
        return;
      }
      popupRef.current = window.open(
        url,
        `integration_${provider}`,
        'popup,width=500,height=600',
      );
      setIsConnecting(true);

      pollTimeoutRef.current = setTimeout(() => {
        clearPolling();
        setIsConnecting(false);
        setNotification({
          type: NotificationType.Error,
          message: 'Connection timed out. Please try again.',
        });
      }, POLL_TIMEOUT_MS);

      pollIntervalRef.current = setInterval(async () => {
        if (popupRef.current?.closed) {
          clearPolling();
          setIsConnecting(false);
          setNotification({
            type: NotificationType.Error,
            message: 'Connection cancelled.',
          });
          return;
        }
        try {
          const statusRes =
            await ThirdPartyIntegrationService.getConnectionStatus(provider);
          const status = statusRes?.data?.status;

          if (status === 'completed') {
            clearPolling();
            await fetchConnections(provider);
            onSuccess?.();
            setIsConnecting(false);
          }
        } catch {
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      loginErrorHandler(err);
    }
  }, [provider, fetchConnections, setNotification, onSuccess, clearPolling]);

  useEffect(() => {
    if (!isConnecting) return;

    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (data?.type !== INTEGRATION_OAUTH_DONE || data?.provider !== provider)
        return;

      clearPolling();
      setIsConnecting(false);

      if (data.success === true) {
        fetchConnections(provider).then(() => onSuccess?.());
      } else {
        setNotification({
          type: NotificationType.Error,
          message: data.error ?? 'Connection was denied or failed.',
        });
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isConnecting, provider, clearPolling, fetchConnections, onSuccess, setNotification]);

  useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  if (children) {
    return <>{children({ onClick: connectionHandler, isConnecting })}</>;
  }

  return (
    <span
      style={{
        fontSize: '14px',
        color: '#3650DE',
        fontWeight: 700,
        cursor: 'pointer',
      }}
      onClick={() => connectionHandler()}
    >
      {isConnecting ? 'Connecting...' : 'Click here'}
    </span>
  );
};

export default ConnectToProvider;
